import type {
  SectionAssignment,
  CompletionMetric,
} from '@breakoutlearning/firebase-repository/models/SectionAssignment'
import type { UseFormSetValue } from 'react-hook-form'
import {
  BreakoutRadioButton,
  BreakoutRadioGroup,
} from 'components/design-system/BreakoutRadioButton'
import { ConfigureGradingScalars } from '../../../assignment/dialogs/ConfigureGradingScalars'
import { useTranslationTyped } from 'i18n/i18n'
import { TriangleAlertIcon } from 'components/icons/TriangleAlert'
import classNames from 'classnames'
import { FormError } from 'components/design-system/form/FormError'
import type { AssignmentFormValues } from 'pages/instructor/slide_deck/AssignmentDialog/schemas'
import { InfoIcon } from 'components/icons/Info'
import { useCallback, useId } from 'react'
import { GradingLineItemSelect } from 'pages/lti/GradingLineItemSelect'
import { BreakoutInfoTooltip } from 'components/design-system/BreakoutPrefabTooltips'
import { BreakoutSwitch } from 'components/design-system/BreakoutSwitch'
import { GradeAppealsSwitch } from './GradeAppealsSwitch'

export const GradingInputs = ({
  setValue,
  configureGrading,
  releasedToStudents,
  hasQuestions,
  hasRubrics,
  scalarsRef,
  initialAssignmentGradingScalars,
  setGradingSumCallback,
  gradingSumValid,
  gradingSum,
  onChange,
  readOnly = false,
  assignment,
  onLtiGradingLineItemIdChange,
  showFinalGradeToParticipants,
  onShowFinalGradeToParticipantsChange,
  gradeAppealsEnabled,
  onGradeAppealsEnabledChange,
  advancedGradingSlot,
  onCustomizeWeights,
  completionMetric,
  onCompletionMetricChange,
}: {
  setValue: UseFormSetValue<AssignmentFormValues>
  configureGrading: boolean | undefined
  releasedToStudents: boolean
  hasQuestions: boolean
  hasRubrics: boolean
  scalarsRef: React.RefObject<
    SectionAssignment['data']['assignmentGradingScalars']
  >
  initialAssignmentGradingScalars: SectionAssignment['data']['assignmentGradingScalars']
  setGradingSumCallback: (gradingSum: {
    isValid: boolean
    sumAsInt: number
  }) => void
  gradingSumValid: boolean
  gradingSum: number
  currentScalarsToCheckDirty?:
    | SectionAssignment['data']['assignmentGradingScalars']
    | null
  onChange?: ({
    scalars,
    configureGrading,
  }: {
    scalars: SectionAssignment['data']['assignmentGradingScalars']
    configureGrading: boolean
  }) => void
  readOnly?: boolean
  assignment?: SectionAssignment
  onLtiGradingLineItemIdChange?: (ltiGradingLineItemId: string) => void
  showFinalGradeToParticipants: boolean
  onShowFinalGradeToParticipantsChange: (checked: boolean) => void
  gradeAppealsEnabled: boolean
  onGradeAppealsEnabledChange: (checked: boolean) => void
  /** Optional custom-rubric panel (score mapping), rendered alongside the
   * grading scalars whenever the deck has eligible rubrics. */
  advancedGradingSlot?: React.ReactNode
  /** Open the weights dialog (the panel itself is always shown). */
  onCustomizeWeights?: () => void
  /** How Completion is measured. Absent = legacy time in session. */
  completionMetric?: CompletionMetric
  /** When provided, the Completion metric toggle renders. */
  onCompletionMetricChange?: (metric: CompletionMetric) => void
}) => {
  const { tt } = useTranslationTyped()
  const labelId = useId()

  // Memoize the onChange handler to prevent unnecessary re-renders
  const handleGradingScalarsChange = useCallback(
    (v: {
      participationScore: number
      quizScore: number
      totalRubricScore: number
      attendanceScore: number
      isValid: boolean
      sumAsInt: number
    }) => {
      setGradingSumCallback(v)
      // Extract just the scalar values to pass to the onChange callback
      const {
        participationScore,
        quizScore,
        totalRubricScore,
        attendanceScore,
      } = v
      onChange &&
        onChange({
          scalars: {
            participationScore,
            quizScore,
            totalRubricScore,
            attendanceScore,
          },
          configureGrading: true,
        })
    },
    [setGradingSumCallback, onChange]
  )

  const handleConfigureGradingTrue = useCallback(() => {
    setValue('configureGrading', true)
    onChange &&
      onChange({
        scalars: scalarsRef.current ?? undefined,
        configureGrading: true,
      })
  }, [setValue, onChange, scalarsRef])

  const handleConfigureGradingFalse = useCallback(() => {
    setValue('configureGrading', false)
    setValue('gradeAppealsEnabled', false, { shouldDirty: true })
    onChange &&
      onChange({
        scalars: scalarsRef.current ?? undefined,
        configureGrading: false,
      })
  }, [setValue, onChange, scalarsRef])

  const radioGroupValue = (() => {
    if (configureGrading === undefined) {
      return null
    }
    if (configureGrading) {
      return 'yes'
    } else {
      return 'no'
    }
  })()

  return (
    <>
      <div className="flex w-full max-w-[450px] flex-col self-center">
        {releasedToStudents && (
          <div className="mb-3 flex flex-row items-center justify-center gap-2 rounded-xl border border-outline-light px-3 py-2">
            <TriangleAlertIcon size={24} className="stroke-core-error" />
            <div className="text-body-medium">
              {tt.instructor_library.published_assignment_grading_warning()}
            </div>
          </div>
        )}
        <h3 className="mb-1 text-title-medium" id={labelId}>
          {tt.instructor_library.grade_your_students()}
        </h3>
        <>
          <BreakoutRadioGroup
            aria-labelledby={labelId}
            value={radioGroupValue}
            onValueChange={(val) => {
              if (val === 'yes') handleConfigureGradingTrue()
              else if (val === 'no') handleConfigureGradingFalse()
            }}
            disabled={readOnly}
            className="flex w-full flex-row gap-1"
          >
            <BreakoutRadioButton
              data-testid="configure-grading-true"
              label={tt.instructor_library.yes()}
              value="yes"
            />
            <BreakoutRadioButton
              data-testid="configure-grading-false"
              label={tt.instructor_library.no()}
              value="no"
            />
          </BreakoutRadioGroup>
          <FormError
            error={
              configureGrading === undefined
                ? {
                    message: tt.instructor_library.grading_required(),
                    type: 'custom',
                  }
                : undefined
            }
          />
        </>
      </div>
      {configureGrading &&
        onLtiGradingLineItemIdChange &&
        assignment?.data?.ltiGradingLineItemOptions?.length && (
          <div className="mt-3">
            <h3 className="flex flex-row gap-1 border-b border-outline-light pb-2 text-title-medium">
              {tt.instructor_library.configure_lti_grading_item()}
              <BreakoutInfoTooltip
                treatReactElementAsString
                content={
                  <div className="max-w-[350px]">
                    {tt.instructor_library.configure_lti_grading_item_tooltip()}
                  </div>
                }
              />
            </h3>

            <GradingLineItemSelect
              assignment={assignment!}
              onChange={(ltiGradingLineItemId) => {
                onLtiGradingLineItemIdChange?.(ltiGradingLineItemId)
              }}
            />
          </div>
        )}
      <div className="mt-3 flex w-full">
        {configureGrading === false && (
          <p className="text-body-large text-on-surface-var">
            {tt.instructor_library.no_grading_description()}
          </p>
        )}
        {configureGrading && (
          <div className="flex w-full flex-row gap-8">
            <div className="flex flex-[3] flex-col gap-3">
              <h3 className="border-b border-outline-light pb-2 text-title-medium">
                {tt.instructor_library.configure_scalars_title()}
              </h3>
              <ConfigureGradingScalars
                // disableAll={releasedToStudents}
                disableAll={readOnly}
                hasQuizQuestions={hasQuestions}
                hasRubrics={hasRubrics}
                ref={scalarsRef}
                defaults={initialAssignmentGradingScalars}
                onChange={handleGradingScalarsChange}
                completionMetric={completionMetric}
                onCompletionMetricChange={onCompletionMetricChange}
              />
              {advancedGradingSlot && (
                <button
                  type="button"
                  data-testid="customize-weights-link"
                  disabled={readOnly}
                  onClick={() => onCustomizeWeights?.()}
                  className="self-start text-left text-label-medium text-on-surface-var underline disabled:opacity-50"
                >
                  {tt.instructor_library.custom_rubrics_advanced_title()}
                </button>
              )}
              <div className="flex flex-col gap-1 border-t border-outline-light pt-2">
                <div className="flex flex-row justify-between">
                  <strong className="text-title-medium">
                    {tt.instructor_library.total()}
                  </strong>
                  <div
                    className={classNames(
                      'flex flex-row items-center gap-1 text-title-medium',
                      {
                        'text-core-error': !gradingSumValid,
                      }
                    )}
                  >
                    {!gradingSumValid && <TriangleAlertIcon size={12.5} />}
                    <strong>{gradingSum + '%'}</strong>
                  </div>
                </div>
                <div className="flex flex-row items-center gap-1 text-label-medium text-on-surface-var">
                  <InfoIcon size={15} />
                  <span>
                    {tt.instructor_library.select_grading_weights_description()}
                  </span>
                </div>
              </div>
              <div className="pr-1.5 pt-4.5">
                <BreakoutSwitch
                  checked={showFinalGradeToParticipants ?? true}
                  disabled={readOnly}
                  onChange={(e) =>
                    onShowFinalGradeToParticipantsChange?.(e.target.checked)
                  }
                  className="w-full"
                  accent={true}
                  labelPosition="left"
                  labelClassName="!pl-0 w-full"
                  label={
                    <div className="flex flex-row items-center gap-1 text-label-large text-on-surface">
                      <label>
                        {tt.instructor_library.display_final_score_to_participants()}
                      </label>
                      <BreakoutInfoTooltip
                        content={tt.instructor_library.display_final_score_to_participants_tooltip()}
                      />
                    </div>
                  }
                  testId="show-final-grade-to-participants"
                  tooltip={tt.instructor_library.display_final_score_to_participants_tooltip()}
                />
                <div className="pt-3">
                  <GradeAppealsSwitch
                    checked={gradeAppealsEnabled}
                    disabled={readOnly}
                    showFinalGradeToParticipants={showFinalGradeToParticipants}
                    onChange={onGradeAppealsEnabledChange}
                  />
                </div>
              </div>
            </div>
            {advancedGradingSlot && (
              <div className="flex flex-[2] flex-col border-l border-outline-light pl-8">
                {advancedGradingSlot}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}
